import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import CostSummaryCard from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostSummaryCard';
import formatQuantity from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatQuantity';
import formatShortDate from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatShortDate';
import resolveReferenceLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveReferenceLabel';
import resolveSubjectLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveSubjectLabel';
import resolveAverageMultiple from '@UI/Pages/Administration/AiCosts/CostTabs/ItemCosts/Helpers/resolveAverageMultiple';
import type { ItemDimension } from '@UI/Pages/Administration/AiCosts/CostTabs/ItemCosts/ItemCostsTable';
import formatEuros from '@UI/Helpers/formatEuros';
import formatAudioDuration from '@UI/Pages/Administration/AiCosts/Helpers/formatAudioDuration';
import type AiCostBreakdownListView from '@UI/Store/AiCosts/Views/AiCostBreakdownListView';
import type AiCostSummaryView from '@UI/Store/AiCosts/Views/AiCostSummaryView';
import { css } from '@emotion/react';
import type * as React from 'react';

const listStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

type Props = {
	readonly list: AiCostBreakdownListView;
	readonly dimension: ItemDimension;
	readonly total: AiCostSummaryView | null;
	readonly reference: AiCostSummaryView | null;
};

/** The rows of the document or audio table as cards, on a narrow screen, closed by the dark Total card. */
const ItemCostCards: React.FC<Props> = ({ list, dimension, total, reference }) => {
	const isAudio = dimension === 'interviews';
	const referenceAverage =
		(isAudio ? reference?.audio.averageCost : reference?.documents.averageCost) ?? null;
	const totalPart = total === null ? null : isAudio ? total.audio : total.documents;

	return (
		<div css={listStyle}>
			{list.list.map((row) => {
				const subject = resolveSubjectLabel(dimension, row);
				const date = formatShortDate(row.processedAt);
				const user = resolveReferenceLabel(row.user, 'Sans utilisateur', 'Utilisateur supprimé');
				const facility = resolveReferenceLabel(row.facility, '—', 'Agence supprimée');
				const isUnpriced = row.unpricedItemCount > 0;

				return (
					<CostSummaryCard
						key={row.subjectId ?? 'unattributed'}
						title={subject.title}
						amount={isUnpriced ? 'Sans tarif' : formatEuros(row.costAmount)}
						lines={[
							isAudio ? `${date} · ${formatAudioDuration(row.audio.durationSeconds)}` : date,
							`${user} · ${facility}`,
						]}
						chip={
							isUnpriced
								? 'Sans tarif : non compté dans le total'
								: resolveAverageMultiple(row.costAmount, referenceAverage)
						}
						isMuted={subject.isMuted}
					/>
				);
			})}
			<ConditionalLoader data={totalPart}>
				{(part) => (
					<CostSummaryCard
						isInverted
						title="Total"
						amount={formatEuros(part.costAmount)}
						lines={[
							isAudio
								? formatQuantity(part.count, 'audio', 'audios')
								: formatQuantity(part.count, 'document', 'documents'),
						]}
					/>
				)}
			</ConditionalLoader>
		</div>
	);
};

export default ItemCostCards;
